import { supabase } from './supabase';
import { getAccessToken } from './supabaseRest';

const BUCKET_NAME = 'pkm-media';

/**
 * Upload a blob to Supabase Storage via the direct REST API using the cached
 * JWT, bypassing the Supabase JS storage client (which is known to hang/stall
 * in this app — see uploadImage's timeout workaround). Returns true on success.
 *
 * Used for reader HTML (`source.html`) capture where a silent JS-client failure
 * previously left source objects with no reader attachment (empty Reader view).
 */
export async function restStorageUpload(
  path: string,
  blob: Blob,
  contentType: string,
  { upsert = true }: { upsert?: boolean } = {}
): Promise<boolean> {
  const token = await getAccessToken();
  if (!token) {
    console.error('[restStorageUpload] No access token — cannot upload', path);
    return false;
  }
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const url = `${supabaseUrl}/storage/v1/object/${BUCKET_NAME}/${path}`;
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': contentType,
        'x-upsert': String(upsert),
        'cache-control': '3600',
      },
      body: blob,
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      console.error(`[restStorageUpload] Upload failed ${res.status} for ${path}:`, text);
      return false;
    }
    return true;
  } catch (err) {
    console.error(`[restStorageUpload] Upload threw for ${path}:`, err);
    return false;
  }
}

/**
 * Initialize storage bucket
 * Checks if bucket exists - doesn't try to create (requires admin access)
 */
export async function initializeStorageBucket() {
  try {
    // Check if user is authenticated first - storage operations need auth
    const storageKey = 'sb-urkfvgqpzeuixgdayrex-auth-token';
    const stored = localStorage.getItem(storageKey);
    if (!stored) {
      // Not authenticated - skip bucket check, will be done after login
      return true;
    }

    // Try to list files in the bucket to verify it exists
    // This is more reliable than listBuckets which may have RLS restrictions
    const { error: listError } = await supabase.storage
      .from(BUCKET_NAME)
      .list('', { limit: 1 });

    if (listError) {
      // Bucket doesn't exist or other error
      if (listError.message.includes('not found') || listError.message.includes('does not exist')) {
        console.warn(`⚠️ Storage bucket "${BUCKET_NAME}" does not exist.`);
        console.warn('📝 Please create it manually in Supabase Dashboard:');
        console.warn('   1. Go to Storage in Supabase Dashboard');
        console.warn(`   2. Create a new bucket named "${BUCKET_NAME}"`);
        console.warn('   3. Set it as Public');
      }
      // App can still work - just won't have image upload capability
      return true;
    }

    // Bucket exists and is accessible
    console.log('✅ Storage bucket ready');
    return true;
  } catch (error) {
    // Don't spam console with storage errors - not critical for app function
    console.debug('[Storage] Bucket check skipped:', error);
    return true;
  }
}

/**
 * Upload an image file to storage
 * Stores in both Supabase (for sync) and IndexedDB (for offline cache)
 */
export async function uploadImage(file: File): Promise<{ url: string; path: string; synced: boolean } | null> {
  try {
    // Get user ID from localStorage directly to avoid supabase.auth.getUser() which can hang
    const storageKey = 'sb-urkfvgqpzeuixgdayrex-auth-token';
    const storedSession = localStorage.getItem(storageKey);

    if (!storedSession) {
      console.error('User not authenticated - no session in localStorage');
      return null;
    }

    let userId: string;
    try {
      const parsed = JSON.parse(storedSession);
      userId = parsed.user?.id;
      if (!userId) {
        console.error('User ID not found in stored session');
        return null;
      }
    } catch (e) {
      console.error('Failed to parse stored session:', e);
      return null;
    }

    // Generate unique filename
    const timestamp = Date.now();
    const randomId = Math.random().toString(36).substring(2, 9);
    const fileExt = file.name.split('.').pop();
    const fileName = `${timestamp}-${randomId}.${fileExt}`;

    // Path includes user_id folder for RLS
    const filePath = `${userId}/images/${fileName}`;

    // Store in IndexedDB for offline access (web reloads images from here)
    await storeImageInIndexedDB(fileName, file);

    // Create blob URL as immediate local display source
    const blobUrl = URL.createObjectURL(file);

    // Upload to Supabase Storage via the direct REST API.
    //
    // The Supabase JS storage client hangs in this app (navigator.locks /
    // BroadcastChannel issues), so the previous implementation used a 5s
    // timeout and silently fell back to a blob URL. That fallback set
    // metadata.imagePath to a path with NO file behind it: the web app hid
    // this by reloading images from IndexedDB, but mobile (which fetches
    // getPublicUrl(imagePath)) got a blank image. restStorageUpload is the
    // proven reliable path (same fix as reader-HTML capture).
    const uploaded = await restStorageUpload(filePath, file, file.type || 'image/*', {
      upsert: false,
    });

    if (!uploaded) {
      console.warn('[uploadImage] Storage upload failed; image will not sync to other devices:', filePath);
      // Local-only: file is in IndexedDB for this browser, but NOT in storage.
      // Return the blob URL for immediate display and NO path — so we never
      // record an imagePath that points at a nonexistent storage object.
      // synced=false tells callers NOT to persist this blob URL as block
      // content (a blob: URL is browser-local and renders blank on mobile /
      // any other device). Callers should keep it in local UI state only.
      return {
        url: blobUrl,
        path: '',
        synced: false,
      };
    }

    // Get public URL from Supabase (pkm-media is a public bucket)
    const { data: { publicUrl } } = supabase.storage
      .from(BUCKET_NAME)
      .getPublicUrl(filePath);

    console.log('✅ Image uploaded to Supabase:', publicUrl);

    return {
      url: publicUrl,
      path: filePath,
      synced: true,
    };
  } catch (error) {
    console.error('Error in uploadImage:', error);
    return null;
  }
}

// IndexedDB version for image storage - bump this if schema changes
const IMAGE_DB_VERSION = 3;

/**
 * Store image in IndexedDB as ArrayBuffer for reliable persistence
 */
async function storeImageInIndexedDB(fileName: string, file: File): Promise<void> {
  // Convert File to ArrayBuffer for reliable IndexedDB storage
  const arrayBuffer = await file.arrayBuffer();

  return new Promise((resolve, reject) => {
    const request = indexedDB.open('PKM_Images', IMAGE_DB_VERSION);

    request.onerror = () => {
      console.error('IndexedDB open error:', request.error);
      reject(request.error);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      // Delete old store if exists (to handle schema changes)
      if (db.objectStoreNames.contains('images')) {
        db.deleteObjectStore('images');
      }
      db.createObjectStore('images', { keyPath: 'fileName' });
      console.log('IndexedDB images store created/recreated');
    };

    request.onsuccess = () => {
      const db = request.result;

      // If store doesn't exist, close and delete database to force recreation
      if (!db.objectStoreNames.contains('images')) {
        console.warn('IndexedDB images store missing, forcing recreation...');
        db.close();
        const deleteRequest = indexedDB.deleteDatabase('PKM_Images');
        deleteRequest.onsuccess = () => {
          // Retry storing after deletion - recursive call
          storeImageInIndexedDB(fileName, file).then(resolve).catch(reject);
        };
        deleteRequest.onerror = () => reject(deleteRequest.error);
        return;
      }

      const transaction = db.transaction(['images'], 'readwrite');
      const store = transaction.objectStore('images');

      const imageData = {
        fileName,
        data: arrayBuffer,  // Store as ArrayBuffer, not File
        mimeType: file.type,
        size: file.size,
        uploadedAt: new Date().toISOString()
      };

      const putRequest = store.put(imageData);

      putRequest.onsuccess = () => {
        console.log('Image stored in IndexedDB:', fileName);
        db.close();
        resolve();
      };
      putRequest.onerror = () => {
        console.error('IndexedDB put error:', putRequest.error);
        db.close();
        reject(putRequest.error);
      };

      transaction.oncomplete = () => db.close();
    };
  });
}

/**
 * Get image from IndexedDB and convert back to File
 */
export async function getImageFromIndexedDB(fileName: string): Promise<File | null> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('PKM_Images', IMAGE_DB_VERSION);

    request.onerror = () => {
      console.error('IndexedDB open error (get):', request.error);
      reject(request.error);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (db.objectStoreNames.contains('images')) {
        db.deleteObjectStore('images');
      }
      db.createObjectStore('images', { keyPath: 'fileName' });
    };

    request.onsuccess = () => {
      const db = request.result;

      // Check if images store exists
      if (!db.objectStoreNames.contains('images')) {
        db.close();
        resolve(null);
        return;
      }

      const transaction = db.transaction(['images'], 'readonly');
      const store = transaction.objectStore('images');
      const getRequest = store.get(fileName);

      getRequest.onsuccess = () => {
        const result = getRequest.result;
        db.close();

        if (!result) {
          resolve(null);
          return;
        }

        // Handle both old (File) and new (ArrayBuffer) formats
        if (result.file instanceof File) {
          // Legacy format - stored as File
          resolve(result.file);
        } else if (result.data instanceof ArrayBuffer) {
          // New format - stored as ArrayBuffer, convert to File
          const file = new File([result.data], fileName, { type: result.mimeType });
          resolve(file);
        } else {
          console.warn('Unknown image data format in IndexedDB');
          resolve(null);
        }
      };

      getRequest.onerror = () => {
        console.error('IndexedDB get error:', getRequest.error);
        db.close();
        reject(getRequest.error);
      };

      transaction.oncomplete = () => db.close();
    };
  });
}

/**
 * Upload a file to storage (PDFs, documents, etc.)
 * Similar to uploadImage but for general files
 */
export async function uploadFile(file: File): Promise<{ url: string; path: string; name: string; size: number; type: string } | null> {
  try {
    // Get user ID from localStorage directly to avoid supabase.auth.getUser() which can hang
    const storageKey = 'sb-urkfvgqpzeuixgdayrex-auth-token';
    const storedSession = localStorage.getItem(storageKey);

    if (!storedSession) {
      console.error('User not authenticated - no session in localStorage');
      return null;
    }

    let userId: string;
    try {
      const parsed = JSON.parse(storedSession);
      userId = parsed.user?.id;
      if (!userId) {
        console.error('User ID not found in stored session');
        return null;
      }
    } catch (e) {
      console.error('Failed to parse stored session:', e);
      return null;
    }

    // Generate unique filename while preserving original name for display
    const timestamp = Date.now();
    const randomId = Math.random().toString(36).substring(2, 9);
    const fileExt = file.name.split('.').pop();
    const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const fileName = `${timestamp}-${randomId}-${sanitizedName}`;

    // Path includes user_id folder for RLS
    const filePath = `${userId}/files/${fileName}`;

    // Create blob URL as fallback (always available immediately)
    const blobUrl = URL.createObjectURL(file);

    // Upload to Supabase Storage with timeout to prevent hanging
    const UPLOAD_TIMEOUT = 10000; // 10 seconds for larger files
    let uploadError: Error | null = null;

    try {
      const uploadPromise = supabase.storage
        .from(BUCKET_NAME)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      const timeoutPromise = new Promise<{ error: Error }>((_, reject) =>
        setTimeout(() => reject(new Error('Upload timeout')), UPLOAD_TIMEOUT)
      );

      const result = await Promise.race([uploadPromise, timeoutPromise]);
      if ('error' in result && result.error) {
        uploadError = result.error;
      }
    } catch (e) {
      uploadError = e instanceof Error ? e : new Error('Upload failed');
    }

    if (uploadError) {
      console.warn('Failed to upload file to Supabase, using local storage only:', uploadError.message);
      // Fall back to blob URL if upload fails or times out
      return {
        url: blobUrl,
        path: filePath,
        name: file.name,
        size: file.size,
        type: file.type
      };
    }

    // Get public URL from Supabase
    const { data: { publicUrl } } = supabase.storage
      .from(BUCKET_NAME)
      .getPublicUrl(filePath);

    console.log('✅ File uploaded to Supabase:', publicUrl);

    return {
      url: publicUrl,
      path: filePath,
      name: file.name,
      size: file.size,
      type: file.type
    };
  } catch (error) {
    console.error('Error in uploadFile:', error);
    return null;
  }
}

/**
 * Delete an image from storage
 */
export async function deleteImage(filePath: string): Promise<boolean> {
  try {
    const { error } = await supabase.storage
      .from(BUCKET_NAME)
      .remove([filePath]);

    if (error) {
      console.error('Error deleting file:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in deleteImage:', error);
    return false;
  }
}

/**
 * Backfill a single image block's file into Supabase Storage.
 *
 * Historically, web image uploads used the JS storage client with a 5s timeout
 * that silently fell back to a blob URL — leaving `metadata.imagePath` pointing
 * at a storage object that was never created. The file lived only in the
 * uploading browser's IndexedDB, so the image showed on web (reloaded from
 * IndexedDB) but was blank on mobile (which fetches getPublicUrl(imagePath)).
 *
 * Given an existing `imagePath` (e.g. `userId/images/123-abc.jpg`), this checks
 * whether the object exists in storage; if not, it reads the file from
 * IndexedDB by filename and uploads it via the reliable REST path.
 *
 * Returns:
 *  - { path } when the file is now present in storage (already there or just uploaded)
 *  - null when it could not be backfilled (not in IndexedDB / upload failed)
 */
export async function backfillImageToStorage(imagePath: string): Promise<{ path: string } | null> {
  if (!imagePath) return null;

  const fileName = imagePath.split('/').pop();
  if (!fileName) return null;

  // Is the object already in storage? A HEAD via the public URL is enough
  // (pkm-media is public). If it 200s, nothing to do.
  try {
    const publicUrl = getImageUrl(imagePath);
    const head = await fetch(publicUrl, { method: 'HEAD' });
    if (head.ok) {
      return { path: imagePath };
    }
  } catch {
    // Network hiccup — fall through and attempt the upload.
  }

  // Not in storage. Recover the original file from this browser's IndexedDB.
  const file = await getImageFromIndexedDB(fileName);
  if (!file) {
    // File isn't cached in this browser — cannot backfill from here.
    return null;
  }

  const uploaded = await restStorageUpload(imagePath, file, file.type || 'image/*', {
    upsert: true,
  });
  if (!uploaded) {
    console.warn('[backfillImageToStorage] Re-upload failed for', imagePath);
    return null;
  }

  console.log('✅ Backfilled image to storage:', imagePath);
  return { path: imagePath };
}

/**
 * Get public URL for an image
 */
export function getImageUrl(filePath: string): string {
  const { data } = supabase.storage
    .from(BUCKET_NAME)
    .getPublicUrl(filePath);

  return data.publicUrl;
}
